// Verify-Resend preflight modal. Runs auth/domain/sender/subscriber checks
// against Resend and surfaces the outcome as a bulleted checklist so the
// admin can self-diagnose before hitting Send.
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PreflightDialog({
  open, onOpenChange, busy, result, onRerun,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="preflight-modal">
        <DialogHeader>
          <DialogTitle>Verify Resend</DialogTitle>
          <DialogDescription>
            Quick self-checks against the Resend account before you broadcast.
          </DialogDescription>
        </DialogHeader>
        {busy ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Running checks…
          </p>
        ) : result ? (
          <div className="space-y-3">
            <div
              className={`text-sm font-medium ${result.ok ? "text-emerald-700" : "text-destructive"}`}
              data-testid="preflight-verdict"
            >
              {result.ok ? "All checks passed — safe to send." : "One or more checks failed — see below."}
            </div>
            <ul className="divide-y divide-border rounded-md border border-border overflow-hidden text-sm" data-testid="preflight-check-list">
              {(result.checks || []).map((c) => (
                <li key={`${c.name}-${c.status}`} className="flex items-start gap-3 p-3" data-testid={`preflight-check-${c.name.toLowerCase().replace(/\W+/g, "-")}`}>
                  <span
                    className={`shrink-0 mt-0.5 inline-block w-2 h-2 rounded-full ${
                      c.status === "pass" ? "bg-emerald-500" : c.status === "warn" ? "bg-amber-500" : "bg-destructive"
                    }`}
                  />
                  <div className="min-w-0">
                    <p className="font-medium">{c.name}</p>
                    <p className={`text-xs mt-0.5 ${c.status === "fail" ? "text-destructive" : "text-muted-foreground"} break-words`}>
                      {c.detail}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={onRerun} data-testid="preflight-rerun">
                <RefreshCw size={13} className="mr-1.5" /> Re-run
              </Button>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="preflight-close">
                Close
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
